import { useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AuthContext } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Sparkles } from "lucide-react";
import { API } from "@/App";
import LegalModal from "@/components/LegalModal";
import { trackSignUp, trackLogin } from "@/lib/analytics";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    language: "es"
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedCommunications, setAcceptedCommunications] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, section: "terms" });
  const [loading, setLoading] = useState(false);

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLogin && !acceptedTerms) {
      toast.error(formData.language === "ca" 
        ? "Has d'acceptar els Termes i Condicions i la Política de Privacitat"
        : "Debes aceptar los Términos y Condiciones y la Política de Privacidad");
      return;
    }

    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : { ...formData, accepted_terms: acceptedTerms, accepted_communications: acceptedCommunications };

      const response = await axios.post(`${API}${endpoint}`, payload);
      const token = response.data.access_token;

      // Get user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const userData = userResponse.data;
      login(token, userData);
      if (isLogin) {
        trackLogin();
        const gender = userData.gender;
        if (userData.language === 'ca') {
          const welcome = gender === 'female' ? 'Benvinguda de nou!' : gender === 'male' ? 'Benvingut de nou!' : 'Benvingut/da de nou!';
          toast.success(welcome);
        } else {
          const welcome = gender === 'female' ? '¡Bienvenida de nuevo!' : '¡Bienvenido de nuevo!';
          toast.success(welcome);
        }
      } else {
        trackSignUp('email', formData.language);
        toast.success(formData.language === 'ca' ? "Compte creat amb èxit!" : "¡Cuenta creada con éxito!");
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.detail || "Error de autenticación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-teal-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-slate-400/10 rounded-full blur-3xl bottom-1/4 right-1/4 animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass rounded-3xl p-8 shadow-2xl border border-teal-400/20">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold glow-text mb-2">
              {isLogin ? "Bienvenido" : "Crear Cuenta"}
            </h2>
            <p className="text-gray-400">
              {isLogin ? "Continúa tu viaje de automaestría" : "Comienza tu transformación"}
            </p>
          </div>

          <form data-testid="auth-form" onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Nombre
                </label>
                <input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  required={!isLogin}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                  placeholder="Tu nombre"
                />
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                data-testid="email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-300 mb-2">
                  Idioma preferido
                </label>
                <select
                  id="language"
                  data-testid="language-select"
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/20 transition-all"
                >
                  <option value="es">Español</option>
                  <option value="ca">Català</option>
                </select>
              </div>
            )}

            {!isLogin && (
              <div className="space-y-3 pt-1">
                {/* Terms & Privacy - Required */}
                <label data-testid="terms-checkbox-label" className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    data-testid="terms-checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-black/30 text-teal-500 focus:ring-teal-400/30 focus:ring-2 cursor-pointer shrink-0"
                  />
                  <span className="text-xs text-gray-400 leading-relaxed">
                    {formData.language === "ca" ? (
                      <>
                        He llegit i accepto els{" "}
                        <button type="button" onClick={() => setLegalModal({ open: true, section: "terms" })} className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                          Termes i Condicions
                        </button>
                        {" "}i la{" "}
                        <button type="button" onClick={() => setLegalModal({ open: true, section: "privacy" })} className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                          Política de Privacitat
                        </button>
                        {" "}*
                      </>
                    ) : (
                      <>
                        He leído y acepto los{" "}
                        <button type="button" onClick={() => setLegalModal({ open: true, section: "terms" })} className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                          Términos y Condiciones
                        </button>
                        {" "}y la{" "}
                        <button type="button" onClick={() => setLegalModal({ open: true, section: "privacy" })} className="text-teal-400 hover:text-teal-300 underline underline-offset-2">
                          Política de Privacidad
                        </button>
                        {" "}*
                      </>
                    )}
                  </span>
                </label>

                {/* Communications - Optional */}
                <label data-testid="communications-checkbox-label" className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    data-testid="communications-checkbox"
                    checked={acceptedCommunications}
                    onChange={(e) => setAcceptedCommunications(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-600 bg-black/30 text-teal-500 focus:ring-teal-400/30 focus:ring-2 cursor-pointer shrink-0"
                  />
                  <span className="text-xs text-gray-400 leading-relaxed">
                    {formData.language === "ca"
                      ? "Accepto rebre comunicacions, emails informatius i recordatoris d'ESSENC·IA"
                      : "Acepto recibir comunicaciones, emails informativos y recordatorios de ESSENC·IA"
                    }
                  </span>
                </label>
              </div>
            )}

            <button
              data-testid="submit-btn"
              type="submit"
              disabled={loading || (!isLogin && !acceptedTerms)}
              className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Procesando..." : (isLogin ? "Iniciar Sesión" : "Crear Cuenta")}
            </button>

            {isLogin && (
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-sm text-gray-400 hover:text-teal-300 transition-colors"
                >
                  ¿Has olvidado tu contraseña?
                </Link>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-mode-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-teal-300 hover:text-teal-200 transition-colors text-sm"
            >
              {isLogin ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
            </button>
          </div>

          {/* Legal links footer */}
          <div className="mt-4 text-center">
            <Link to="/legal?section=terms" className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors">
              {formData.language === "ca" ? "Termes i Condicions" : "Términos y Condiciones"}
            </Link>
            <span className="text-gray-700 mx-2">·</span>
            <Link to="/legal?section=privacy" className="text-[11px] text-gray-600 hover:text-gray-400 transition-colors">
              {formData.language === "ca" ? "Política de Privacitat" : "Política de Privacidad"}
            </Link>
          </div>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal
        isOpen={legalModal.open}
        onClose={() => setLegalModal({ ...legalModal, open: false })}
        section={legalModal.section}
        defaultLanguage={formData.language}
      />
    </div>
  );
};

export default AuthPage;